<!-- how to handle multiple languages. -> Vuetify config -->
<template>
  <div>
    <v-menu
        v-model="isToggleVisible"
        :close-on-content-click="false"
        offset-y
        :position-x="menuCoordinates.x"
        :position-y="menuCoordinates.y"
        :transition="false"
        nudge-bottom="20"
    >
      <v-card>
        <v-list dense>
          <v-list-item class="ml-1">
            <span class="text-subtitle-2" :style="{color: this.$vuetify.theme.dark ? '#EEEEEE' : '' }">
              <!--@slot Internal text for the header in a context menu responsible for columns display-->
              <slot name="columnsMenuHeader">
                {{ $t('colVisibility') }}
              </slot>
            </span>
          </v-list-item>
          <v-divider class="mx-2"></v-divider>
          <v-list-item dense>
            <v-list-item-action class="my-0 mr-2">
              <v-checkbox dense v-model="isAllVisible" @click="resetColumns"/>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ $t('showAll') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="mx-2"></v-divider>
          <div v-for="item in localHeaders" :key="item.value">
            <v-list-item dense v-if="item.value !== 'actions'">
              <v-list-item-action class="my-0 mr-2">
                <v-checkbox dense v-model="item.visible" @change="emitVisibleColumns"/>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </div>
        </v-list>
      </v-card>
    </v-menu>
    <!--bind v-model from parent value and v-on=$listeners -->
    <!--Vue 3 doesn't have listeners anymore.-->
    <v-data-table
        :class="{'headers-no-wrap': !headersWrap}"
        v-bind="$attrs"
        :headers="visibleHeaders"
        v-on="$listeners"
        :value="value"
        :items-per-page="$attrs['items-per-page'] ? $attrs['items-per-page'] :25"
        :footer-props="{
        'items-per-page': 25,
        'items-per-page-options': [5,15, 25, 50, 100],
      }">

      <template v-for="header in visibleHeaders" v-slot:[`header.${header.value}`]>
        <v-tooltip v-if="header.headerTooltips" left :key="header.value" bottom color="grey lighten-3"
                   class="tooltip-border">
          <template v-slot:activator="{ on }">
            <v-icon :size="iconSize" v-on="on">mdi-information-outline</v-icon>
          </template>
          <div class="my-1">
            <span v-if="header.startTextTooltips" class="black--text">{{ header.startTextTooltips }}</span>
          </div>
          <v-divider></v-divider>
          <div v-for="(v,k) in header.headerTooltips" :key="k" class="py-1">
            <v-icon :color="v.color ? v.color : null" :size="iconSize">{{ v.icon }}</v-icon>
            <span class="black--text"> {{ v.text }}</span>
          </div>
        </v-tooltip>
        <span :key="header.text" v-if="!header.textDisable">{{ header.text }}</span>
        <v-btn v-if="header.value === 'actions' && toggleHeaders"
               :key="header.value" icon x-small @click="showColumnToggle($event)">
          <v-icon :size="iconSize">mdi-tune</v-icon>
        </v-btn>
      </template>

      <template v-for="h in $attrs.headers" v-slot:[`item.${h.value}`]="{ item }">

        <TcnTableCell v-if="h.type.toLowerCase() === 'text'" :key="h.value" :openDelay="tooltipOpenDelay"
                      :tdTooltipPosition="tdTooltipPosition"
                      :headerLength=" h.value.length" :cellValue="getValue(item, h.value)">
          <div :title="titles ? getValue(item, h.value) : ''">
            {{ getValue(item, h.value) }}
          </div>
          <template v-slot:tooltipValue>
            <span>{{ getValue(item, h.value) }}</span>
          </template>
        </TcnTableCell>

        <TcnTableCell v-if="h.type.toLowerCase() === 'number'" :key="h.value" :openDelay="tooltipOpenDelay"
                      :tdTooltipPosition="tdTooltipPosition"
                      :headerLength=" h.value.length" :cellValue="getValue(item, h.value)">
          <div :title="titles ? parseFloat(getValue(item, h.value)).toLocaleString() : ''">
            {{ getValue(item, h.value) ? parseFloat(getValue(item, h.value)).toLocaleString() : null }}
          </div>
          <template v-slot:tooltipValue>
            <span>{{ parseFloat(getValue(item, h.value)).toLocaleString() }}</span>
          </template>
        </TcnTableCell>

        <TcnTableCell v-if="h.type.toLowerCase() === 'list'" :key="h.value" :openDelay="tooltipOpenDelay"
                      :tdTooltipPosition="tdTooltipPosition"
                      :headerLength=" h.value.length" :cellValue="getValue(item, h.value)">
          <div :title="titles ? getTitleFromList(getValue(item, h.value), h.listAttribute) : ''">
            {{ getTitleFromList(getValue(item, h.value), h.listAttribute) }}
          </div>
          <template v-slot:tooltipValue>
            <span> {{ getTitleFromList(getValue(item, h.value), h.listAttribute) }}</span>
          </template>
        </TcnTableCell>

        <div v-if="h.type.toLowerCase() === 'chip'" :key="h.value">
          <v-chip v-if="getValue(item, h.value) !== undefined" :small="h.options.small" :large="h.options.large"
                  :color="h.options.color">
            {{ getValue(item, h.value) }}
          </v-chip>
        </div>

        <div v-if="h.type.toLowerCase() === 'chip_text'" :key="h.value">
          <v-chip @click="h.click(item,h)" :small="h.options && h.options.small" :large="h.options && h.options.large"
                  :color="h.options && h.options.color">
            {{ h.options ? h.options.text : '' }}
          </v-chip>
        </div>

        <div v-else-if="h.type.toLowerCase() === 'text_link'" :key="h.value" style="min-width: 250px">
          <v-hover v-slot="{ hover }">
            <router-link
                :class="hover ? 'link light-blue--text text--lighten-1':'text-decoration-none'"
                :to="{
              name: h.to,
              params: {
               [h.paramsText]: item[h.paramValue]
              }}">
              {{ getValue(item, h.value) }}
            </router-link>
          </v-hover>
        </div>

        <div v-else-if="h.type.toLowerCase() === 'icon'" :key="h.value">
          <v-icon class="mr-4"
                  v-if="h.icons" :size="iconSize"
                  :color="h.icons.find((element) => element.value === getValue(item, h.value))
                  ? h.icons.find((element) => element.value === getValue(item, h.value)).color
                  : null"
          >
            {{ getIcon(h, item) }}
          </v-icon>
        </div>

        <div v-else-if="h.type.toLowerCase() === 'icon_actions'" :key="h.value">
          <span v-for="(v,k) in h.iconsActions" :key="k">
            <v-icon v-if="v.show(item)" class="mr-1" @click="v.click(item, h)"
                    :title="'title' in v ? (typeof v.title === 'function' ? v.title(item,h) : v.title): ''">
              {{ v.icon }}
            </v-icon>
          </span>
        </div>

        <div v-else-if="h.type.toLowerCase() === 'icon_tooltip'" :key="h.value">
          <v-tooltip
              :left="h.icons.find((element) => element.value === getValue(item, h.value)).left"
              :right="h.icons.find((element) => element.value === getValue(item, h.value)).right"
              :top="h.icons.find((element) => element.value === getValue(item, h.value)).top"
              :bottom="h.icons.find((element) => element.value === getValue(item, h.value)).bottom"
          >
            <template v-slot:activator="{on, attrs}">
              <v-icon class="mr-4"
                      :size="iconSize"
                      :color="h.icons.find((element) => element.value === getValue(item, h.value)).color"
                      v-bind="attrs"
                      v-on="on"
              >
                {{ h.icons ? h.icons.find((element) => element.value === getValue(item, h.value)).icon : '' }}
              </v-icon>
            </template>
            <span>{{ h.icons.find((element) => element.value === getValue(item, h.value)).text }}</span>
          </v-tooltip>
        </div>

        <div v-else-if="h.type.toLowerCase() === 'btn_action_icon'" :key="h.value">
          <v-btn
              color="primary"
              class="my-1 mx-0 px-0"
              @click="h.click(item, h)"
          >
            <v-icon>
              {{ h.icon }}
            </v-icon>
          </v-btn>
        </div>

        <div v-else-if="h.type.toLowerCase() === 'checkbox'" :key="h.value">
          {{ getValue(item, h.value) ? 'Tak' : 'Nie' }}
        </div>

        <div v-else-if="h.type.toLowerCase() === 'checkbox_action'" :key="h.value">
          <v-checkbox
              v-if="item.id !== $keycloak.tokenParsed.sub"
              :value="getValue(item, h.value)"
              class="my-0"
              hide-details
              @click="h.click(item, h)"
          />
        </div>

        <div v-else-if="h.type.toLowerCase() === 'icon_action'" :key="h.value">
          <v-icon class="mr-4" @click="h.click(item, h)">
            {{ h.icons.icon }}
          </v-icon>
        </div>

        <TcnTableCell v-else-if="h.type.toLowerCase() === 'date'" :key="h.value" :openDelay="tooltipOpenDelay"
                      :tdTooltipPosition="tdTooltipPosition"
                      :headerLength=" h.value.length" :cellValue="getValue(item, h.value)">
          <span
              :title="titles ? (getValue(item, h.value) ? $moment(getValue(item, h.value)).format(h.tooltipFormat? h.tooltipFormat : h.format ? h.format : 'DD.MM.YYYY') : '') : ''">
            {{
              getValue(item, h.value) ? $moment(getValue(item, h.value)).format(h.format ? h.format : 'DD.MM.YYYY') : ''
            }}
          </span>
          <template v-slot:tooltipValue>
          <span> {{
              getValue(item, h.value) ? $moment(getValue(item, h.value)).format(h.tooltipFormat? h.tooltipFormat : h.format ? h.format : 'DD.MM.YYYY') : ''
            }}
          </span>
          </template>
        </TcnTableCell>

        <TcnTableCell v-else-if="h.type.toLowerCase() === 'datetime'" :key="h.value" :openDelay="tooltipOpenDelay"
                      :tdTooltipPosition="tdTooltipPosition" :headerLength=" h.value.length"
                      :cellValue="getValue(item, h.value)">
          <span :title="titles ? ((getValue(item, h.value) ? $moment(getValue(item, h.value)).format('DD.MM.YYYY') : '') + ' '
               + (getValue(item, h.value) ? $moment(getValue(item, h.value)).format('HH:mm:ss') : '')) : ''">
            {{ getValue(item, h.value) ? $moment(getValue(item, h.value)).format('DD.MM.YYYY') : '' }}
            <span v-if="item[h.value]" class="ml-2"></span>
            {{ getValue(item, h.value) ? $moment(getValue(item, h.value)).format('HH:mm:ss') : '' }}
          </span>
          <template v-slot:tooltipValue>
          <span>
            {{
              (getValue(item, h.value) ? $moment(getValue(item, h.value)).format('DD.MM.YYYY') : '') + ' ' + (getValue(item, h.value) ? $moment(getValue(item, h.value)).format('HH:mm:ss') : '')
            }}
          </span>
          </template>
        </TcnTableCell>
      </template>

      <template v-for="(slot, name) in $scopedSlots" v-slot:[name]="item">
        <slot :name="name" v-bind="item"></slot>
      </template>

      <template v-slot:footer.page-text="{item}">

      </template>

      <template #footer.page-text="{pageStart, pageStop, itemsLength}">
        <TcnTableFooter
            :page="$attrs.options ? $attrs.options.page : 0"
            :items-per-page="$attrs.options ? $attrs.options.itemsPerPage : 25"
            :page-start="pageStart"
            :page-stop="pageStop"
            :items-length="itemsLength"
            @count="$emit('count')"
        />
      </template>


    </v-data-table>
  </div>
</template>

<script>
import TcnTableCell from './TcnTableCell';
//ES12 structuredClone()
import {cloneDeep, get} from 'lodash';
import TcnTableFooter from "./TcnTableFooter";
export default {
  name: 'TcnDataTable',
  emits: ['columnsState'],
  components: {
    TcnTableFooter,
    TcnTableCell
  },
  props: {
    /** Used for controlling selected rows */
    value: [Object, Array],
    iconSize: [String, Number],
    /** Tooltips open delay in ms. */
    tooltipOpenDelay: {
      type: [String, Number],
      default: 500
    },
    /** Custom tooltips made as divs */
    titles: {
      type: Boolean,
      default: false,
    },
    /** Tooltip position. {'top' | 'right' | 'bottom' | 'left': true} */
    tdTooltipPosition: {
      type: Object,
      default() {
        return {'bottom': true};
      },
    },
    /** Showing additional column with context menu responsible for toggling showed columns */
    toggleHeaders: {
      type: Boolean,
      default: true,
    },
    headersWrap: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      sort: [],
      menuCoordinates: {},
      isToggleVisible: false,
      localHeaders: {},
    };
  },
  computed: {
    visibleHeaders() {
      return this.localHeaders.filter(header => header.visible);
    },
    isAllVisible: {
      get() {
        return this.localHeaders.every(header => header.visible === true)
      },
      set() {
      }
    }
  },
  methods: {
    getValue(object, path) {
      // return object?.[path];
      //TODO Is lodash even needed here?
      return get(object, path);
    },
    getTitleFromList(list, index) {
      return list.map((val) => val[index]).join(', ');
    },
    getIcon(h, item) {
      return h.icons ? h.icons.find((element) => element.value === this.getValue(item, h.value)).icon : '';
    },
    emitVisibleColumns() {
      this.$emit('columnsState', this.visibleHeaders.filter(header => header.value !== 'actions'));
      this.localHeaders = [...this.localHeaders]
    },
    showColumnToggle(ev) {
      this.menuCoordinates.x = ev.clientX;
      this.menuCoordinates.y = ev.clientY;
      this.isToggleVisible = true;
    },
    resetColumns() {
      if (this.isAllVisible) {
        this.localHeaders.forEach(header => header.visible = header.value === 'actions' ? true : false);
      } else {
        this.localHeaders.forEach(header => header.visible = true);
      }
      this.emitVisibleColumns();
    },
    prepareHeaders() {
      this.localHeaders = Array.isArray(this.$attrs.headers) ? cloneDeep(this.$attrs.headers) : [];
      const isToggle = this.localHeaders.some(header => header.value === 'actions');

      if (this.toggleHeaders && !isToggle) {
        this.localHeaders.push(
            {
              text: '',
              value: 'actions',
              visible: true,
              sortable: false,
              filterable: false,
              groupable: false,
              align: 'right',
              width: '1px',
              class: 'px-0'
            });

      }
      this.localHeaders.forEach(header => {
        if (!('visible' in header)) header.visible = true;
      });
    }

  },
  watch: {
    '$attrs.headers': {
      handler() {
        this.prepareHeaders()
      },
      immediate: true
    }
  },
  // created() {

  //   //window.addEventListener('resize', () => this.isToggleVisible = false);
  // },
  destroyed() {
    //Nie zadziała, referencje są rozne
    //window.removeEventListener('resize', () => this.isToggleVisible = false);
  },
  i18n: {
    messages: {
      en: {
        "showAll": "Show all",
        "colVisibility": "Column visibility"
      },
      pl: {
        "showAll": "Pokaż wszystkie",
        "colVisibility": "Widoczność kolumn"
      }
    },
  },
  mounted() {
    //this.$moment.tz.setDefault("America/New_York")
    console.log(this.$moment())
  }
};
</script>

<style scoped lang="scss">
.two-liner {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1; /* number of lines to show */
  -webkit-box-orient: vertical;
}

.link {
  text-underline-offset: 2px;
}

.tooltip-border {
  border: 5px solid #000;
}

::v-deep .headers-no-wrap .v-data-table-header th {
  padding: 0 8px !important;
  white-space: nowrap;
}

::v-deep :not(.headers-no-wrap) .v-data-table-header th {
  padding: 0 8px !important;

}

::v-deep tbody td {
  word-break: break-all;
  padding: 0 8px !important;
}

::v-deep tbody td div {
  @extend .two-liner;
}
</style>

